import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import type { CashRoundingRule } from '@bbpos/utils';
import { PageContainer, Skeleton, ErrorBoundary, useToast } from '@bbpos/ui';
import { formatCurrency } from '@/utils/currency';
import { useTenantCurrency } from '@/hooks/use-tenant-currency';
import { useSalesOrder, useCreateInvoice, SalesOrderStatusBadge } from '@/features/sales-orders';
import { FileTextIcon } from '../quotations/icons';
import { LineQuantityForm } from './LineQuantityForm';
import { InvoiceCashRounding, useDefaultCashRounding } from './InvoiceCashRounding';

function SalesOrderInvoiceContent() {
  const currency = useTenantCurrency();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { data: order, isLoading, error } = useSalesOrder(id);
  const invoiceMutation = useCreateInvoice();
  const defaultRounding = useDefaultCashRounding(currency, order?.location_id);
  // Off by default: cash rounding is for cash, and the payment mode is not known
  // yet. Switching it on starts from the Settings rule (SS4-002 decision).
  const [rounding, setCashRounding] = useState<CashRoundingRule | null>(null);

  if (error) {
    toast({ title: (error as Error).message, variant: 'error' });
    navigate('/sales-orders');
    return null;
  }
  if (isLoading || !order) {
    return (
      <>
        <Skeleton variant="rect" height={96} className="rounded-2xl mb-6" />
        <Skeleton variant="rect" height={420} className="rounded-2xl" />
      </>
    );
  }
  if (order.status === 'COMPLETED' || order.status === 'CANCELLED') {
    toast({ title: `Cannot raise an invoice — order is ${order.status.toLowerCase()}`, variant: 'error' });
    navigate(`/sales-orders/${id}`);
    return null;
  }

  return (
    <>
      <div className="relative overflow-hidden rounded-2xl bg-gradient-to-r from-brand-600 via-indigo-500 to-indigo-600 p-6 text-white shadow-[0_24px_60px_-24px_rgba(37,99,235,0.55)] mb-6 animate-fade-in">
        <div className="pointer-events-none absolute -top-24 -right-24 h-56 w-56 rounded-full bg-white/20 blur-3xl" />
        <div className="pointer-events-none absolute -bottom-20 -left-10 h-48 w-48 rounded-full bg-black/10 blur-3xl" />
        <div className="relative flex items-center justify-between gap-4">
          <div className="min-w-0">
            <div className="flex items-center gap-3">
              <div className="rounded-lg bg-white/15 p-2 backdrop-blur-sm ring-1 ring-white/20"><FileTextIcon className="h-5 w-5" /></div>
              <h1 className="text-2xl font-bold tracking-tight text-white">Raise invoice</h1>
            </div>
            <p className="text-brand-100 mt-1 mono-num">
              {order.so_number} <span className="text-brand-200/70">·</span> {order.customer_name}
              <span className="text-brand-200/70"> · PO {order.customer_po_number}</span>
            </p>
          </div>
          <SalesOrderStatusBadge status={order.status} size="md" />
        </div>
      </div>

      <InvoiceCashRounding currency={currency} value={rounding} defaultRule={defaultRounding} onChange={setCashRounding} />

      <LineQuantityForm
        mode="invoice"
        order={order}
        submitting={invoiceMutation.isPending}
        onBack={() => navigate(`/sales-orders/${id}`)}
        onSubmit={(items, meta) => {
          if (!id) return;
          invoiceMutation.mutate(
            {
              id,
              payload: {
                items, invoice_date: meta.date || undefined, due_date: meta.secondDate || undefined, notes: meta.notes || undefined,
                cash_rounding: rounding,
              },
            },
            {
              onSuccess: (inv) => {
                toast({ title: `Invoice ${inv.invoice_number} raised — ${formatCurrency(inv.total_amount, currency)}`, variant: 'success' });
                // Payments and the PDF live on the invoice itself (Finance → Sales Invoices).
                navigate(`/sales-invoices/${inv.id}`);
              },
              onError: (err: Error) => toast({ title: err.message, variant: 'error' }),
            },
          );
        }}
      />
    </>
  );
}

export function SalesOrderInvoicePage() {
  const { id } = useParams<{ id: string }>();
  return (
    <PageContainer
      title="Raise invoice"
      breadcrumbs={[
        { label: 'Sales Orders', href: '/sales-orders' },
        { label: id?.slice(0, 8).toUpperCase() ?? '', href: `/sales-orders/${id}` },
        { label: 'Invoice' },
      ]}
    >
      <ErrorBoundary>
        <SalesOrderInvoiceContent />
      </ErrorBoundary>
    </PageContainer>
  );
}
